import React, { FC } from 'react';

import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import CampaignTwoToneIcon from '@mui/icons-material/CampaignTwoTone';
import MessageTwoToneIcon from '@mui/icons-material/MessageTwoTone';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { ContactButton } from '../../../components/UI/Buttons';
import useIsMobile from '../../../hooks/useIsMobile';
import { BlueSeparator } from './How';

interface FeatureProps {
  title: string;
  description: string;
  finalText: string;
  video: string;
  reverse: boolean;
  bgColor: string;
  icon: React.ReactNode;
}

const Feature: FC<FeatureProps> = ({ title, description, video, reverse, bgColor, icon, finalText }) => {
  const { isMobile } = useIsMobile();

  return (
    <Box
      sx={{
        py: { xs: 12, md: 16 },
        bgcolor: bgColor,
        borderRadius: '0 0 60px 60px',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={6} direction={reverse ? 'row-reverse' : 'row'} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box>
              <Box display="flex" alignItems="center" mb={3}>
                {icon}
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: { xs: '30px', md: '36px' },
                    fontWeight: 'bold',
                    ml: 2,
                  }}
                >
                  {title}
                </Typography>
              </Box>
              <Box width="100px" mb={4}>
                <BlueSeparator />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Jost',
                  fontSize: { xs: '18px', md: '20px' },
                  lineHeight: 1.7,
                  opacity: 0.85,
                  textAlign: 'justify',
                }}
              >
                {description}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Jost',
                  fontSize: { xs: '18px', md: '20px' },
                  lineHeight: 1.7,
                  opacity: 0.85,
                  mt: 2,
                  display: 'inline-block',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: -2,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'rgb(249, 208, 10)',
                  },
                }}
              >
                {finalText}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                textAlign: 'center',
                video: {
                  maxWidth: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '16px',
                  boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      'linear-gradient(90deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,0.05) 50%, rgba(0,0,0,0.02) 100%)',
                    pointerEvents: 'none',
                  },
                  height: isMobile ? '200px' : { sm: '400px', md: '500px' },
                  width: '100%',
                  aspectRatio: isMobile ? '16/9' : 'auto',
                }}
              >
                <video
                  src={video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  disablePictureInPicture
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const FeaturesDescriptionZeller: FC = () => {
  const features = [
    {
      id: 'feature-atención-leads',
      icon: <MessageTwoToneIcon fontSize="large" />,
      title: 'Atención de Leads',
      description:
        'Recibimos mensajes de tus clientes desde formularios, WhatsApp o campañas en redes sociales. Los atendemos al instante, incluso fuera del horario laboral. Respondemos con información actualizada en tiempo real sobre tu negocio y tus clientes, para ofrecer la mejor atención posible. Identificamos y calificamos los leads automáticamente. Los dejamos listos en tu CRM para que sigas la conversación cuando quieras.',
      finalText: 'Conectamos. Calificamos. Convertimos.',
      video: '/landing/leads.mp4',
      reverse: false,
      bgColor: '#f9f9f9',
    },
    {
      id: 'feature-agendamiento',
      icon: <CalendarMonthTwoToneIcon fontSize="large" />,
      title: 'Agendamientos',
      description:
        'Coordinamos agendamientos sin intervención humana. Recibimos la solicitud, identificamos lo que el cliente necesita, consultamos disponibilidad en tus sistemas y confirmamos la cita. Ya sea una visita, test drive o mantención, lo agendamos y notificamos al instante. También hacemos seguimiento para asegurar su asistencia. Tu equipo solo tiene que recibirlo en sucursal.',
      finalText: 'Agendamos por ti. Tú solo recibes al cliente.',
      video: '/landing/agendamiento.mp4',
      reverse: true,
      bgColor: '#ffffff',
    },
    {
      id: 'feature-campana-outbound',
      icon: <CampaignTwoToneIcon fontSize="large" />,
      title: 'Campañas Outbound',
      description:
        'Lanzamos campañas outbound que generan conversaciones reales. Activamos a tus clientes con mensajes personalizados y medibles. Iniciamos la conversación directamente por WhatsApp, con contenido relevante, ofertas, recordatorios o novedades. Todo automatizado, con seguimiento en tiempo real. Segmentamos, escribimos, enviamos y respondemos. Nos encargamos de todo el flujo, desde el primer contacto hasta la conversión.',
      finalText: 'Tus campañas ahora tienen respuesta.',
      video: '/landing/outbound.mp4',
      reverse: false,
      bgColor: '#f9f9f9',
    },
  ];

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(90deg, #15183A, #224267)',
          py: { xs: 8, md: 12 },
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontFamily: 'Roboto',
                fontSize: { xs: '36px', md: '52px' },
                fontWeight: 'bold',
                mb: 3,
                maxWidth: '900px',
                mx: 'auto',
              }}
            >
              Soluciones que soportan el crecimiento de tu negocio
            </Typography>
            <Box display="flex" justifyContent="center" mb={3}>
              <Box width="400px">
                <BlueSeparator />
              </Box>
            </Box>
            <ContactButton
              variant="contained"
              size="large"
              onClick={() => window.open('https://zcal.co/i/O1PPkO_D', '_blank', 'noopener,noreferrer')}
            >
              Agenda una demo
            </ContactButton>
          </Box>
        </Container>
      </Box>

      {features.map((feature) => (
        <Feature
          key={feature.id}
          icon={feature.icon}
          title={feature.title}
          description={feature.description}
          finalText={feature.finalText}
          video={feature.video}
          reverse={feature.reverse}
          bgColor={feature.bgColor}
        />
      ))}
    </>
  );
};

export default FeaturesDescriptionZeller;
